/**
 * QR Connect Page Component
 * Invisible route handler for /qr-connect endpoint
 * 
 * This component processes QR connection requests silently:
 * - Reads scannedUserId and scannerUserId from URL query params
 * - Validates params are strings and properly formatted
 * - Calls handleQRConnection() to process the connection
 * - Returns hidden div with result status
 * - No visible UI - completely invisible to users
 * 
 * HARDENED: Strict validation, no re-renders, single execution.
 */

import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { handleQRConnection } from "../octopus/QRConnectMiddleman";

/**
 * Validates userId format: alphanumeric, 6-36 characters
 * Enforces: /^[A-Za-z0-9]{6,36}$/
 * @param userId - The user ID to validate
 * @returns true if valid, false otherwise
 */
function isValidUserIdFormat(userId: string): boolean {
  if (typeof userId !== 'string') {
    return false;
  }
  // Alphanumeric only, 6-36 characters - enforced regex
  const userIdRegex = /^[A-Za-z0-9]{6,36}$/;
  return userIdRegex.test(userId);
}

export default function QRConnectPage() {
  const [params] = useSearchParams();
  const scannedUserIdParam = params.get("scannedUserId");
  const scannerUserIdParam = params.get("scannerUserId");
  const [result, setResult] = useState<"processing" | "success" | "error">("processing");
  const hasProcessed = useRef(false); // Prevent multiple executions - useRef guard

  useEffect(() => {
    // useRef guard: prevent multiple executions
    if (hasProcessed.current) {
      return;
    }

    async function process() {
      const timestamp = new Date().toISOString();
      
      // Mark as processed immediately to prevent double execution
      hasProcessed.current = true;

      console.log(`[AUDIT] ${timestamp} QR Connect Page - Processing request:`, {
        scannedUserId: scannedUserIdParam,
        scannerUserId: scannerUserIdParam,
        url: window.location.href
      });

      // Strict null guards: params must be strings (not null)
      if (scannedUserIdParam === null || scannerUserIdParam === null) {
        const error = "Missing required parameters";
        console.error(`[AUDIT] ${timestamp} Validation failed:`, {
          error,
          scannedUserId: scannedUserIdParam,
          scannerUserId: scannerUserIdParam
        });
        setResult("error");
        console.log(`[AUDIT] ${timestamp} QRConnectPage result: error - ${error}`);
        return; // Exit immediately after processing
      }

      // Runtime type guards: ensure they are strings
      if (typeof scannedUserIdParam !== 'string' || typeof scannerUserIdParam !== 'string') {
        const error = "Invalid parameter types";
        console.error(`[AUDIT] ${timestamp} Validation failed:`, {
          error,
          scannedUserIdType: typeof scannedUserIdParam,
          scannerUserIdType: typeof scannerUserIdParam
        });
        setResult("error");
        console.log(`[AUDIT] ${timestamp} QRConnectPage result: error - ${error}`);
        return; // Exit immediately after processing
      }

      // Trim all inputs before validating
      const trimmedScannedUserId = scannedUserIdParam.trim();
      const trimmedScannerUserId = scannerUserIdParam.trim();

      // Validate not empty after trimming
      if (!trimmedScannedUserId || trimmedScannedUserId === '') {
        const error = "scannedUserId cannot be empty";
        console.error(`[AUDIT] ${timestamp} Validation failed:`, {
          error,
          scannedUserId: scannedUserIdParam
        });
        setResult("error");
        console.log(`[AUDIT] ${timestamp} QRConnectPage result: error - ${error}`);
        return; // Exit immediately after processing
      }

      if (!trimmedScannerUserId || trimmedScannerUserId === '') {
        const error = "scannerUserId cannot be empty";
        console.error(`[AUDIT] ${timestamp} Validation failed:`, {
          error,
          scannerUserId: scannerUserIdParam
        });
        setResult("error");
        console.log(`[AUDIT] ${timestamp} QRConnectPage result: error - ${error}`);
        return; // Exit immediately after processing
      }

      // Validate format: alphanumeric, 6-36 characters (same regex)
      if (!isValidUserIdFormat(trimmedScannedUserId)) {
        const error = "scannedUserId format invalid";
        console.error(`[AUDIT] ${timestamp} Validation failed:`, {
          error,
          scannedUserId: trimmedScannedUserId
        });
        setResult("error");
        console.log(`[AUDIT] ${timestamp} QRConnectPage result: error - ${error}`);
        return; // Exit immediately after processing
      }

      if (!isValidUserIdFormat(trimmedScannerUserId)) {
        const error = "scannerUserId format invalid";
        console.error(`[AUDIT] ${timestamp} Validation failed:`, {
          error,
          scannerUserId: trimmedScannerUserId
        });
        setResult("error");
        console.log(`[AUDIT] ${timestamp} QRConnectPage result: error - ${error}`);
        return; // Exit immediately after processing
      }

      try {
        const res = await handleQRConnection(
          trimmedScannedUserId,
          trimmedScannerUserId
        );

        if (res.success) {
          console.log(`[AUDIT] ${timestamp} QR Connect Page - Success:`, res);
          setResult("success");
          console.log(`[AUDIT] ${timestamp} QRConnectPage result: success - ${JSON.stringify(res)}`);
        } else {
          console.error(`[AUDIT] ${timestamp} QR Connect Page - Failed:`, res);
          setResult("error");
          console.log(`[AUDIT] ${timestamp} QRConnectPage result: error - ${res.error || 'unknown'}`);
        }
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        console.error(`[AUDIT] ${timestamp} QR Connect Page - Exception:`, {
          error,
          stack: e instanceof Error ? e.stack : undefined
        });
        setResult("error");
        console.log(`[AUDIT] ${timestamp} QRConnectPage result: error - ${error}`);
      }
      // Exit immediately after processing - no navigation, no visible state updates
    }

    process();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run once on mount, exit immediately after

  // Return 100% invisible UI - no flicker, no exposure
  // display: none + visibility: hidden + zero size
  return (
    <div style={{ display: "none", visibility: "hidden", position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
      {result}
    </div>
  );
}
